'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { Input, InputProps } from '../input';

export interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;

  /**
   * Transform value before setting in form
   */
  transform?: {
    input?: (value: unknown) => string;
    output?: (value: string) => unknown;
  };

  /**
   * Additional props to pass to the Input component
   */
  inputProps?: Omit<InputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref' | 'error'>;
}

/**
 * Input component integrated with React Hook Form
 * Automatically handles form registration and validation
 */
export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  showError = true,
  transform,
  inputProps,
}: FormInputProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  // Apply input transform if provided
  const displayValue = React.useMemo(() => {
    if (transform?.input) {
      return transform.input(value);
    }
    return value || '';
  }, [value, transform]);

  // Handle change with output transform
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (transform?.output) {
        onChange(transform.output(newValue));
      } else {
        onChange(newValue);
      }
    },
    [onChange, transform]
  );

  return (
    <Input
      {...inputProps}
      ref={ref}
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={onBlur}
      error={showError ? error?.message : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
    />
  );
}

FormInput.displayName = 'FormInput';
